import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Clock, Award, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact-form`, {
        ...formData,
        form_type: 'landing_page'
      });
      toast.success('Thank you! We\'ll contact you shortly.');
      setFormData({ name: '', email: '', country_code: '+1', phone: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <Shield className="h-5 w-5" />, text: 'Federal trademark protection' },
    { icon: <Clock className="h-5 w-5" />, text: '24-hour filing guarantee' },
    { icon: <Award className="h-5 w-5" />, text: 'Expert case review' },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Helmet>
        <title>Protect Your Brand Today - Trademark Registration from $45 | Trademark Venus</title>
        <meta name="description" content="Limited time offer! Professional trademark registration starting at just $45 plus federal filing fees. Fast 24-hour filing, expert guidance, comprehensive search included. Secure your brand now!" />
        <meta name="keywords" content="trademark registration offer, cheap trademark filing, affordable trademark, trademark deal, USPTO registration discount" />
      </Helmet>
      {/* Hero Section with Form */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Content */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-400 mb-4 block">
                Limited Time Offer
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-tight mb-6">
                Protect Your Brand{' '}
                <span className="bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
                  Today
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8">
                Fast, professional trademark registration services starting at just $45 + federal filing fees. Secure your brand identity with expert guidance every step of the way.
              </p>

              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="bg-red-50 rounded-full p-2 text-red-700">
                      {feature.icon}
                    </div>
                    <span className="text-slate-700 font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-4">
                  Why Choose Us?
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Comprehensive trademark search included</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Direct USPTO filing with expert review</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Dedicated case manager for support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Trademark monitoring and alerts</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Right Form */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-red-100 sticky top-24">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                    Get Started Now
                  </h2>
                  <p className="text-slate-600">
                    Fill out the form to begin your trademark registration
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="lp-name">Name</Label>
                    <Input
                      id="lp-name"
                      data-testid="lp-name-input"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-email">Email</Label>
                    <Input
                      id="lp-email"
                      data-testid="lp-email-input"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-phone">Contact Number</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger className="w-[120px] h-12" data-testid="lp-country-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1 (US)</SelectItem>
                          <SelectItem value="+44">+44 (UK)</SelectItem>
                          <SelectItem value="+91">+91 (IN)</SelectItem>
                          <SelectItem value="+61">+61 (AU)</SelectItem>
                          <SelectItem value="+86">+86 (CN)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="lp-phone"
                        data-testid="lp-phone-input"
                        placeholder="123-456-7890"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value.length <= 11) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        required
                        className="flex-1 h-12"
                        maxLength={11}
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-message">Tell us about your trademark</Label>
                    <Textarea
                      id="lp-message"
                      data-testid="lp-message-input"
                      placeholder="Describe your trademark and business needs..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="lp-submit-button"
                    className="w-full h-12 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full shadow-lg transition-all duration-300"
                  >
                    {loading ? 'Submitting...' : 'Start My Application'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to our terms and privacy policy
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Trusted by Thousands of Businesses
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Join the businesses that have successfully protected their brands with Trademark Venus. Our streamlined process and expert support make trademark registration simple and stress-free.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
